import { SdkError, SdkErrorCode } from '../errors/sdkErrors';
import type { Transport } from '../shared/transport';
import type { AuthInfo, JSONRPCMessage, RequestId } from '../types/index';

interface QueuedMessage {
    message: JSONRPCMessage;
    extra?: { authInfo?: AuthInfo };
}

/**
 * In-memory transport for creating clients and servers that talk to each other within the same process.
 *
 * Intended for testing and development. For production in-process connections, use
 * `StreamableHTTPClientTransport` against a local server URL.
 */
export class InMemoryTransport implements Transport {
    private _otherTransport?: InMemoryTransport;
    private _messageQueue: QueuedMessage[] = [];
    private _closed = false;

    onclose?: () => void;
    onerror?: (error: Error) => void;
    onmessage?: (message: JSONRPCMessage, extra?: { authInfo?: AuthInfo }) => void;
    sessionId?: string;

    /**
     * Creates a pair of linked in-memory transports that can communicate with each other. One should be passed to a {@linkcode @modelcontextprotocol/client!client/client.Client | Client} and one to a {@linkcode @modelcontextprotocol/server!server/server.Server | Server}.
     */
    static createLinkedPair(): [InMemoryTransport, InMemoryTransport] {
        const clientTransport = new InMemoryTransport();
        const serverTransport = new InMemoryTransport();
        clientTransport._otherTransport = serverTransport;
        serverTransport._otherTransport = clientTransport;
        return [clientTransport, serverTransport];
    }

    async start(): Promise<void> {
        // Process any messages that were queued before start was called
        while (this._messageQueue.length > 0) {
            const queuedMessage = this._messageQueue.shift()!;
            this.onmessage?.(queuedMessage.message, queuedMessage.extra);
        }
    }

    async close(): Promise<void> {
        if (this._closed) return;
        this._closed = true;

        const other = this._otherTransport;
        this._otherTransport = undefined;
        try {
            await other?.close();
        } finally {
            this.onclose?.();
        }
    }

    /**
     * Sends a message with optional auth info.
     * This is useful for testing authentication scenarios.
     */
    async send(message: JSONRPCMessage, options?: { relatedRequestId?: RequestId; authInfo?: AuthInfo }): Promise<void> {
        if (!this._otherTransport) {
            throw new SdkError(SdkErrorCode.NotConnected, 'Not connected');
        }

        if (this._otherTransport.onmessage) {
            this._otherTransport.onmessage(message, { authInfo: options?.authInfo });
        } else {
            this._otherTransport._messageQueue.push({ message, extra: { authInfo: options?.authInfo } });
        }
    }
}
